import React, { useState, useMemo, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Snackbar,
    Chip,
    CircularProgress,
    Tabs,
    Tab,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    ArrowForward as BackIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import useFetch from '../../../../../hooks/useFetch';
import useDeleteItem from '../../../../../hooks/useDeleteItem';
import RTL from '../../../../../utils/RTL';
import { toPersianDigits } from '../../../../../utils/convertNumbers';
import { API } from '../../../../../utils/apiRoutes';
import StandardTable from '../../../../common/table/StandardTable';
import TeacherSalaryForm from './TeacherSalaryForm';
import api from '../../../../../config/axiosConfig';

function TeacherSalaryManagement() {
    const theme = useTheme();
    const [personType, setPersonType] = useState('teacher'); // 'teacher' | 'staff'
    const [filters, setFilters] = useState({
        teacher_id: '',
        staff_id: '',
        is_active: '',
    });
    const [formDialogOpen, setFormDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [activateDialogOpen, setActivateDialogOpen] = useState(false);
    const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
    const [selectedSalary, setSelectedSalary] = useState(null);
    const [salaryToDelete, setSalaryToDelete] = useState(null);
    const [salaryToActivate, setSalaryToActivate] = useState(null);
    const [salaryToDeactivate, setSalaryToDeactivate] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [refreshToken, setRefreshToken] = useState(0);

    const { deleteItem, isLoading: isDeleting } = useDeleteItem();

    // Build API params
    const apiParams = useMemo(() => {
        const params = { person_type: personType };
        if (filters.teacher_id) params.teacher_id = filters.teacher_id;
        if (filters.staff_id) params.staff_id = filters.staff_id;
        if (filters.is_active !== '') params.is_active = filters.is_active === 'true' ? 'true' : 'false';
        return params;
    }, [filters, personType]);

    const isStaffTab = personType === 'staff';
    const personLabel = isStaffTab ? 'پرسنل' : 'آموزگار';

    // Fetch salaries
    const { data: salariesData, loading: isLoading, error, refetch } = useFetch(
        API.teacherInstituteSalaries,
        { params: apiParams }
    );

    // Refetch when refreshToken changes
    useEffect(() => {
        if (refreshToken > 0) {
            refetch();
        }
    }, [refreshToken, refetch]);

    const salaries = salariesData?.results || salariesData || [];

    const handleAdd = () => {
        setSelectedSalary(null);
        setFormDialogOpen(true);
    };

    const handleEdit = (salary) => {
        setSelectedSalary(salary);
        setFormDialogOpen(true);
    };

    const handleDelete = (salary) => {
        setSalaryToDelete(salary);
        setDeleteDialogOpen(true);
    };

    const handleActivate = (salary) => {
        setSalaryToActivate(salary);
        setActivateDialogOpen(true);
    };

    const handleDeactivate = (salary) => {
        setSalaryToDeactivate(salary);
        setDeactivateDialogOpen(true);
    };

    const confirmDelete = async () => {
        if (!salaryToDelete) return;

        try {
            await deleteItem(`${API.teacherInstituteSalaries}${salaryToDelete.id}/`);
            setSnackbar({ 
                open: true, 
                message: 'حقوق با موفقیت حذف شد.', 
                severity: 'success' 
            });
            setRefreshToken(prev => prev + 1);
        } catch (err) {
            setSnackbar({ 
                open: true, 
                message: err?.response?.data?.detail || 'خطا در حذف حقوق', 
                severity: 'error' 
            });
        } finally {
            setDeleteDialogOpen(false);
            setSalaryToDelete(null);
        }
    };

    const confirmActivate = async () => {
        if (!salaryToActivate) return;

        try {
            await api.post(`${API.teacherInstituteSalaries}${salaryToActivate.id}/activate/`);
            setSnackbar({ 
                open: true, 
                message: 'حقوق با موفقیت فعال شد.', 
                severity: 'success' 
            });
            setRefreshToken(prev => prev + 1);
        } catch (err) {
            setSnackbar({ 
                open: true, 
                message: err?.response?.data?.detail || 'خطا در فعال‌سازی حقوق', 
                severity: 'error' 
            });
        } finally {
            setActivateDialogOpen(false);
            setSalaryToActivate(null);
        }
    };

    const confirmDeactivate = async () => {
        if (!salaryToDeactivate) return;

        try {
            await api.post(`${API.teacherInstituteSalaries}${salaryToDeactivate.id}/deactivate/`);
            setSnackbar({ 
                open: true, 
                message: 'حقوق با موفقیت غیرفعال شد.', 
                severity: 'success' 
            });
            setRefreshToken(prev => prev + 1);
        } catch (err) {
            setSnackbar({ 
                open: true, 
                message: err?.response?.data?.detail || 'خطا در غیرفعال‌سازی حقوق', 
                severity: 'error' 
            });
        } finally {
            setDeactivateDialogOpen(false);
            setSalaryToDeactivate(null);
        }
    };

    const handleFormSuccess = () => {
        const wasEdit = !!selectedSalary;
        setFormDialogOpen(false);
        setSelectedSalary(null);
        setRefreshToken(prev => prev + 1);
        // Trigger refetch immediately
        setTimeout(() => {
            refetch();
        }, 100);
        setSnackbar({ 
            open: true, 
            message: wasEdit ? 'حقوق با موفقیت ویرایش شد.' : 'حقوق با موفقیت ثبت شد.', 
            severity: 'success' 
        });
    };

    const formatCurrency = (amount) => {
        return toPersianDigits(new Intl.NumberFormat('fa-IR').format(amount || 0));
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            return toPersianDigits(date.toLocaleDateString('fa-IR'));
        } catch {
            return dateString;
        }
    };

    // Table columns
    const columns = [
        {
            id: 'person_name',
            label: personLabel,
            align: 'start',
            render: (salary) => (
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {salary.staff_name || salary.teacher_name || '-'}
                </Typography>
            )
        },
        {
            id: 'institute_name',
            label: 'موسسه',
            align: 'start',
            priority: 'secondary',
            render: (salary) => (
                <Typography variant="body2">
                    {salary.institute_name || '-'}
                </Typography>
            )
        },
        {
            id: 'fixed_monthly_salary',
            label: 'حقوق\nماهیانه',
            render: (salary) => (
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {formatCurrency(salary.fixed_monthly_salary)} تومان
                </Typography>
            )
        },
        {
            id: 'start_date',
            label: 'تاریخ\nشروع',
            priority: 'secondary',
            render: (salary) => (
                <Typography variant="body2">
                    {formatDate(salary.start_date)}
                </Typography>
            )
        },
        {
            id: 'end_date',
            label: 'تاریخ\nپایان',
            priority: 'secondary',
            render: (salary) => (
                <Typography variant="body2">
                    {formatDate(salary.end_date)}
                </Typography>
            )
        },
        {
            id: 'is_active',
            label: 'وضعیت',
            compact: true,
            render: (salary) => (
                <Chip
                    label={salary.is_active ? 'فعال' : 'غیرفعال'}
                    size="small"
                    color={salary.is_active ? 'success' : 'default'}
                />
            )
        },
        {
            id: 'actions',
            label: 'عملیات',
            compact: true,
            render: (salary) => (
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    {!salary.is_active && (
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleActivate(salary);
                            }}
                            color="success"
                            aria-label="فعال‌سازی"
                        >
                            <CheckCircleIcon fontSize="small" />
                        </IconButton>
                    )}
                    {salary.is_active && (
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDeactivate(salary);
                            }}
                            color="warning"
                            aria-label="غیرفعال‌سازی"
                        >
                            <CancelIcon fontSize="small" />
                        </IconButton>
                    )}
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(salary);
                        }}
                        color="primary"
                        aria-label="ویرایش"
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(salary);
                        }}
                        color="error"
                        aria-label="حذف"
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Box>
            )
        },
    ];

    return (
        <RTL>
            <Box>
                {formDialogOpen ? (
                    /* Full-page create/edit form (drill-down) */
                    <Box sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                            <Button
                                startIcon={<BackIcon />}
                                variant="outlined"
                                onClick={() => setFormDialogOpen(false)}
                            >
                                بازگشت به فهرست
                            </Button>
                            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                {selectedSalary ? 'ویرایش حقوق ماهیانه' : 'افزودن حقوق ماهیانه'}
                            </Typography>
                        </Box>
                        <Paper sx={{ p: 3 }}>
                            <TeacherSalaryForm
                                salary={selectedSalary}
                                defaultPersonType={personType}
                                onSuccess={handleFormSuccess}
                                onCancel={() => setFormDialogOpen(false)}
                            />
                        </Paper>
                    </Box>
                ) : (
                <>
                <Typography variant="h4" sx={{ mb: 3, textAlign: 'center', fontWeight: 'bold' }}>
                    حقوق های ماهیانه
                </Typography>

                <Paper sx={{ p: 3, mb: 3 }}>
                    <Tabs
                        value={personType}
                        onChange={(e, value) => setPersonType(value)}
                        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
                    >
                        <Tab value="teacher" label="آموزگاران" />
                        <Tab value="staff" label="پرسنل" />
                    </Tabs>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        <Typography variant="h6">
                            {isStaffTab ? 'حقوق‌های ماهیانه پرسنل' : 'حقوق‌های ماهیانه آموزگاران'}
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAdd}
                        >
                            {isStaffTab ? 'افزودن حقوق پرسنل' : 'افزودن حقوق آموزگار'}
                        </Button>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            خطا در بارگذاری داده‌ها: {error.message || error}
                        </Alert>
                    )}

                    <StandardTable
                        columns={columns}
                        rows={salaries}
                        loading={isLoading}
                        error={error}
                        emptyText={isStaffTab ? 'هیچ حقوق ماهیانه‌ای برای پرسنل ثبت نشده است.' : 'هیچ حقوق ماهیانه‌ای برای آموزگاران ثبت نشده است.'}
                        onRowClick={handleEdit}
                        getRowKey={(row) => row.id}
                    />
                </Paper>
                </>
                )}

                {/* Delete Confirmation Dialog */}
                <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                    <DialogTitle>تأیید حذف</DialogTitle>
                    <DialogContent>
                        <Typography>
                            آیا از حذف حقوق ماهیانه {salaryToDelete?.staff_name || salaryToDelete?.teacher_name} اطمینان دارید؟
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteDialogOpen(false)}>انصراف</Button>
                        <Button onClick={confirmDelete} color="error" variant="contained" disabled={isDeleting}>
                            {isDeleting ? 'در حال حذف...' : 'حذف'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Activate Confirmation Dialog */}
                <Dialog open={activateDialogOpen} onClose={() => setActivateDialogOpen(false)}>
                    <DialogTitle>تأیید فعال‌سازی</DialogTitle>
                    <DialogContent>
                        <Typography>
                            آیا می‌خواهید حقوق ماهیانه {salaryToActivate?.staff_name || salaryToActivate?.teacher_name} را فعال کنید؟
                            در صورت وجود حقوق فعال دیگر برای این {salaryToActivate?.staff_name ? 'پرسنل' : 'آموزگار'}، آن‌ها غیرفعال خواهند شد.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setActivateDialogOpen(false)}>انصراف</Button>
                        <Button onClick={confirmActivate} color="success" variant="contained">
                            فعال‌سازی
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Deactivate Confirmation Dialog */}
                <Dialog open={deactivateDialogOpen} onClose={() => setDeactivateDialogOpen(false)}>
                    <DialogTitle>تأیید غیرفعال‌سازی</DialogTitle>
                    <DialogContent>
                        <Typography>
                            آیا می‌خواهید حقوق ماهیانه {salaryToDeactivate?.staff_name || salaryToDeactivate?.teacher_name} را غیرفعال کنید؟
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeactivateDialogOpen(false)}>انصراف</Button>
                        <Button onClick={confirmDeactivate} color="warning" variant="contained">
                            غیرفعال‌سازی
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Snackbar */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </RTL>
    );
}

export default TeacherSalaryManagement;

